import classNames from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import { Switch } from "components/DesignSystem/Switch/Switch";
import { Badge, TaskType } from "components/Task/Badge";
import { ConditionalLink } from "components/conditionalLink";
import { Divider } from "components/design/Divider";
import { Preview } from "components/PreviewModal";
import { Cross } from "components/icons/Cross";
import { PlusIcon } from "components/icons/PlusIcon";
import { NOT_STARTED } from "constants/taskStatus";
import dayjs from "dayjs";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  useGetSliderDataQuery,
  useUpdateGroupDataMutation,
} from "store/apis/salesCrm";
import { FileIcon } from "components/design/FileIcon";
import { DynamicStar } from "components/DesignSystem/Star/DynamicStar";
import { useMarkCompanyAsVipMutation } from "store/apis/crm";
import { DISPLAY_DATE } from "constants/date";
import { useGetGroupDataQuery } from "store/apis/group";
import {
  useGetEntityBanksQuery,
  useRequestAccountantAccessMutation,
} from "store/apis/bankConnections";
import { EntityBankConnections } from "components/BooksCrm/EntityBankConnections";
import { ENTITY_BANK_CONNECTION_DETAILS } from "types/Models/bookkeepingTracker";
import { BackendError } from "types/utils/error";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";

type Tags = {
  uuid: string;
  tag_category: number;
  title: string;
  color_hex: string;
  bg_color_hex: string;
};

const LastQuoteContent = ({
  lastQuoteSent,
  groupId,
}: {
  lastQuoteSent?: { uuid: string; name: string };
  groupId: string;
}) => {
  const { isOpen, close, open } = useModal();
  return (
    <div className="t-flex t-justify-between t-h-8 t-items-center">
      <div className="t-text-caption t-text-text-30">QUOTE</div>
      {lastQuoteSent ? (
        <Button customType="ghost_icon" onClick={open}>
          <FileIcon fileType="PDF" size="32" />
        </Button>
      ) : (
        <ConditionalLink target="_blank" to={`crm/${groupId}/quotes`}>
          <Button customType="icon">
            <PlusIcon color="#41395C" />
          </Button>
        </ConditionalLink>
      )}
      <Preview
        showModal={isOpen}
        closeModal={close}
        groupId={groupId}
        previewId={lastQuoteSent?.uuid}
      />
    </div>
  );
};

const ReferrerContent = ({
  email,
  coupon_code,
  referer_text,
}: {
  email?: string;
  coupon_code?: string;
  referer_text?: string;
}) => {
  const attribuionExists = email || coupon_code || referer_text;
  return (
    <div className="t-flex t-justify-between t-h-8 t-items-center">
      <div className="t-text-caption t-text-text-30">REFERRER</div>
      <div className="t-text-body-sm">
        {attribuionExists && email ? (
          <span>{email}</span>
        ) : (
          <span>{coupon_code || referer_text || "-"}</span>
        )}
      </div>
    </div>
  );
};

const TagsContent = ({ tags }: { tags?: Tags[] }) => {
  return (
    <div className="t-flex t-justify-between t-min-h-8 t-items-center">
      <div className="t-text-caption t-text-text-30">TAGS</div>
      <div className="t-flex t-gap-2 t-flex-wrap t-justify-end t-w-3/5">
        {tags ? (
          tags?.map(({ title, color_hex, bg_color_hex, uuid }) => (
            <div
              key={uuid}
              className="t-rounded-lg t-px-1 t-text-body-sm"
              style={{
                color: color_hex,
                backgroundColor: bg_color_hex,
              }}
            >
              #{title}
            </div>
          ))
        ) : (
          <>-</>
        )}
      </div>
    </div>
  );
};

const Content = ({
  name,
  status,
  isBadge,
  type,
  filingUuid,
  isBank,
}: {
  name: string;
  status: any;
  isBadge?: boolean;
  type?: TaskType;
  filingUuid?: string;
  isBank?: boolean;
}) => {
  return (
    <div className="t-flex t-justify-between t-h-8 t-items-center">
      <div
        className={classNames({
          "t-text-text-100 t-text-body-sm": filingUuid || isBank,
          "t-text-text-30 t-text-caption": !filingUuid && !isBank,
        })}
      >
        {filingUuid ? (
          <ConditionalLink
            to={`/filings/${filingUuid}`}
            target="_blank"
            className="all:unset hover:t-text-purple hover:t-cursor-pointer hover:!t-underline"
          >
            {name}
          </ConditionalLink>
        ) : (
          name
        )}
      </div>
      {status ? (
        !isBadge ? (
          <div className="t-text-body-sm">{status}</div>
        ) : (
          <Badge type={type || "ACTION_REQUIRED"}>{status}</Badge>
        )
      ) : (
        <>-</>
      )}
    </div>
  );
};

const GroupBankConnections = ({ groupId }: { groupId: string }) => {
  const { data: groupData } = useGetGroupDataQuery({ groupId });

  if (!groupData?.entities || groupData.entities.length === 0) {
    return (
      <Badge className="!t-max-w-56 !t-line-clamp-none" type="ACTION_REQUIRED">
        No entities found
      </Badge>
    );
  }

  return (
    <div className="t-flex t-flex-col t-gap-6">
      {groupData.entities.map((entity, index) => (
        <EntityBankConnectionsWrapper
          key={entity.uuid}
          entityId={entity.uuid}
          entityName={entity.name}
          groupId={groupId}
          channelId={entity.bookkeeping_channel ?? null}
          isLastEntity={index === groupData.entities.length - 1}
        />
      ))}
    </div>
  );
};

const EntityBankConnectionsWrapper = ({
  entityId,
  entityName,
  groupId,
  channelId,
  isLastEntity,
}: {
  entityId: string;
  entityName: string;
  groupId: string;
  channelId: string | null;
  isLastEntity: boolean;
}) => {
  const history = useHistory();

  const { alertToast } = useToast();
  const [requestAccountantAccess] = useRequestAccountantAccessMutation();
  const [isRequestingAccess, setIsRequestingAccess] = useState(false);

  const {
    data: ledgerBanks,
    isLoading,
    isSuccess,
  } = useGetEntityBanksQuery({
    groupId,
    entityId,
  });

  const connectionDetails = useMemo((): ENTITY_BANK_CONNECTION_DETAILS[] => {
    if (!ledgerBanks?.items) return [];

    return ledgerBanks.items.map((item) => ({
      name: item.bank_brand?.name,
      connection_available: item.connection_available,
      accountant_access: item.accountant_access,
      accountant_access_reason: item.accountant_access_reason,
      entity_item_id: item.uuid,
      status: item.status,
      logo_url: item.bank_brand?.logo ?? null,
      account_count: 0,
      is_all_disconnected: false,
      accounts: [],
    })) as ENTITY_BANK_CONNECTION_DETAILS[];
  }, [ledgerBanks, entityName]);

  const handleRequestAccess = async (item: {
    entity_item_id: string;
    name: string;
  }) => {
    if (!channelId) return;

    setIsRequestingAccess(true);

    try {
      await requestAccountantAccess({
        entityId,
        entityItemIds: [item.entity_item_id],
        channelId,
      }).unwrap();
    } catch (e) {
      alertToast({ message: (e as BackendError).data?.error?.message });
      setIsRequestingAccess(false);
      return;
    }

    setIsRequestingAccess(false);
    history.push(`/admin/chat/${channelId}`);
  };

  return (
    <Async.Root
      isLoading={isLoading}
      isSuccess={isSuccess}
      isEmpty={!connectionDetails.length}
      customLoader={
        <div className="t-flex t-flex-col t-gap-3">
          <Skeleton className="t-h-16 t-w-full" />
          <Skeleton className="t-h-16 t-w-full" />
        </div>
      }
    >
      <Async.Success>
        <div>
          <EntityBankConnections
            entityId={entityId}
            connectionDetails={connectionDetails}
            onRequestAccess={handleRequestAccess}
            isRequestingAccess={isRequestingAccess}
          />
          {!isLastEntity && <Divider classNames="t-mt-4" />}
        </div>
      </Async.Success>
      <Async.Empty>
        <></>
      </Async.Empty>
    </Async.Root>
  );
};

export const OPSSlider = ({
  groupId,
  setOpenSlider,
}: {
  groupId: string;
  setOpenSlider: (newState: React.SetStateAction<boolean>) => void;
}) => {
  const { pathname } = useLocation();
  const [booksPayWall, setBooksPayWall] = useState<boolean | null>(null);
  const { alertToast, successToast } = useToast();

  const { data } = useGetSliderDataQuery({
    groupId,
    base_task_template_id:
      process.env.PUBLIC_BASE_TASK_TEMPLATE_ID_STATE_FRANCHISE_TAX,
  });

  const [updateGroup, { isLoading, originalArgs }] =
    useUpdateGroupDataMutation();
  const [markGroupAsVip] = useMarkCompanyAsVipMutation();

  useEffect(() => {
    if (booksPayWall !== null) {
      const updateBooksPayWall = async () => {
        try {
          await updateGroup({
            payLoad: { is_books_subscription_required: booksPayWall },
            groupId: groupId,
          });
          setBooksPayWall(null);
        } catch (err: any) {
          alertToast({ message: err?.data?.error?.message });
        }
      };
      updateBooksPayWall();
    }
  }, [booksPayWall]);

  const {
    name,
    assigned_agent,
    books_software = [],
    connected_banks = [],
    is_books_subscription_required,
    last_called,
    last_quote_sent,
    ongoing_tasks = [],
    ongoing_tasks_count = 0,
    subscription_name,
    referer,
    coupon_code,
    referer_text,
    tags = [],
    task: task1099,
    uuid,
    pending_dues_allowed,
    is_vip,
  } = data || {};

  const markCompanyGroupAsVip = async () => {
    try {
      await markGroupAsVip({ groupId }).unwrap();
      successToast({
        message: "Customer marked with a star",
      });
    } catch (e: any) {
      alertToast({ message: e?.data?.error?.message });
    }
  };

  const onDebtWallChange = async (checked: boolean) => {
    try {
      await updateGroup({
        payLoad: { pending_dues_allowed: !checked },
        groupId: groupId,
      });
    } catch (err: any) {
      alertToast({ message: err?.data?.error?.message });
    }
  };

  let taskStatus = "Not Created";
  if (task1099?.current_state_name) {
    taskStatus = task1099.current_state_name;
  }
  if (task1099?.uuid) {
    taskStatus = NOT_STARTED;
  }

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="t-h-full t-flex t-flex-col t-relative"
    >
      <div className="t-flex t-justify-between t-px-6 t-py-4 t-drop-shadow-drawer t-sticky t-top-0 t-bg-surface t-justify-self-start t-items-center">
        <div className="t-text-subtitle t-flex t-items-center t-gap-2">
          <div>{name}</div>
          <div
            className={classNames("-t-mt-1", {
              "t-cursor-pointer": !is_vip,
            })}
            onClick={() => !is_vip && markCompanyGroupAsVip()}
          >
            <DynamicStar isActive={is_vip!} />
          </div>
        </div>
        <Button
          customType="icon"
          size="small"
          onClick={() => setOpenSlider(false)}
        >
          <Cross color="currentColor" />
        </Button>
      </div>
      <div className="t-px-8 t-pt-4 t-pb-32 t-flex t-flex-col t-gap-5 t-overflow-auto t-min-h-[550px]">
        <div className="t-flex t-flex-col t-gap-3">
          <TagsContent tags={tags} />
          <Content
            name="AGENT"
            status={assigned_agent?.name}
            type="FILING_IN_PROGRESS"
            isBadge
          />
          <Content name="TASK 1099" status={taskStatus} isBadge />
          <ReferrerContent
            email={referer?.email}
            referer_text={referer_text}
            coupon_code={coupon_code}
          />
          <LastQuoteContent groupId={groupId} lastQuoteSent={last_quote_sent} />
          <Content name="SOFTWARE" status={books_software} />
          <Content
            name="LAST CALLED"
            status={
              last_called?.date
                ? dayjs(last_called?.date)?.format(DISPLAY_DATE)
                : "-"
            }
          />
          <Content name="SUBSCRIPTION" status={subscription_name} />
          <div className="t-flex t-justify-between t-h-8 t-items-center">
            <div className="t-text-caption t-text-text-30">BOOKS PAY WALL</div>
            <div className="t-flex t-items-center t-gap-1">
              <Switch
                disabled={isLoading}
                checked={Boolean(is_books_subscription_required)}
                name="booksPaywallSwitch"
                onCheckedChange={() => {
                  setBooksPayWall(!is_books_subscription_required);
                }}
              />
            </div>
          </div>

          <div className="t-flex t-justify-between t-h-8 t-items-center">
            <div className="t-text-caption t-text-text-30">DEBT WALL</div>
            <div className="t-flex t-items-center t-gap-1">
              <Switch
                disabled={isLoading}
                checked={!pending_dues_allowed}
                name="paywallSwitch"
                onCheckedChange={onDebtWallChange}
              />
            </div>
          </div>
        </div>

        <Divider />
        <div className="t-flex t-flex-col t-gap-3">
          <div className="t-text-caption t-text-text-30">FILINGS</div>
          {ongoing_tasks_count > 0 ? (
            <>
              {ongoing_tasks?.map(
                ({
                  name,
                  current_state: { name: currentStateName, type },
                  uuid,
                }) => (
                  <Content
                    key={uuid}
                    name={name}
                    status={currentStateName || "Not Started"}
                    type={type}
                    isBadge
                    filingUuid={uuid}
                  />
                )
              )}
            </>
          ) : (
            <Badge
              className="!t-max-w-56 !t-line-clamp-none"
              type="ACTION_REQUIRED"
            >
              No filings
            </Badge>
          )}
        </div>
        <Divider />
        <div className="t-flex t-flex-col t-gap-3">
          <div className="t-text-caption t-text-text-30">BANKS DETAILS</div>
          {connected_banks?.length > 0 ? (
            <GroupBankConnections groupId={groupId} />
          ) : (
            <Badge
              className="!t-max-w-56 !t-line-clamp-none"
              type="ACTION_REQUIRED"
            >
              No bank accounts connected
            </Badge>
          )}
        </div>
      </div>
      <div className="t-w-full t-bg-surface t-shadow-tasks-slider-bottom t-px-3 t-py-4 t-justify-self-end t-sticky t-bottom-0">
        <ConditionalLink to={`${pathname}/${uuid}`}>
          <span>
            <Button customType="primary" block>
              Go to company group
            </Button>
          </span>
        </ConditionalLink>
      </div>
    </div>
  );
};
